import { useCallback, useMemo, useState } from 'react';
import useSWR from 'swr';
import { useDispatch, useSelector } from 'react-redux';
import { INextStep } from '@modules/ContactManager/components/Header/interfaces';
import { refreshWorkflow,  setRefreshWorkflow, setWorkflowNextStep } from '@store/reducers/contactManagerSlice';
import { AlertModal } from '@shared/components/modals/AlertModal';
import { SERVICES } from '@shared/constants/apiConfig';
import { DealsButton } from '@styles/StyledButtons';
import { queryParams } from '@shared/commonFunctions';
import { Roles } from '@shared/constants/constants';
import { TextInputField } from '@shared/fields/TextInputField';
import { useUserAccount } from '@shared/hooks/useUserAccount';
import { getError } from '@shared/utils/helpers';
import { TableModal } from '@shared/components/modals/TableModal';
import { IDeal, ILenderComment, IPushAppData } from '../interfaces';
import { requestGetCreditReports, requestPushApp, requestPushAppToDT, requestPushToOpenLending } from '../queries';
import { LENDER_CODES, meridianLenders } from './constants';
import { AutoLoansModal } from './AutoLoans/AutoLoansModal';
import { FlagshipOverrideModal } from './AutoLoans/FlagshipOverrideModal';
import { IAutoLoans } from './AutoLoans/interfaces';
import { MeridianCommentModal } from './MeridianCommentModal';
import { CulsCudlConfirmationModal } from './CulsCudlConfirmationModal';

interface IPushAppProps {
  data?: IDeal;
  refreshDealsTable?: () => Promise<void>;
}

const dealertrackFormConfig = [
  {
    width: '100%',
    name: 'lenderComment',
    label: 'Add Lender Comment Below? (Optional)',
    multiline: true,
    maxRows: 4,
    component: TextInputField,
  },
];

export const PushButtons = ({ data, refreshDealsTable }: IPushAppProps) => {
  const dispatch = useDispatch();
  const params = queryParams();
  const { userHasRoles } = useUserAccount();
  const refreshWorkflowSection = useSelector(refreshWorkflow);

  const [openPushAppModal, setOpenPushAppModal] = useState(false);
  const [showGrossUpWarning, setShowGrossUpWarning] = useState(false);
  const [pendingPushType, setPendingPushType] = useState<'standard' | 'dealertrack' | 'openLending' | null>(null);

  const [openPushAppDTModal, setOpenPushAppDTModal] = useState(0);
  const [showDTData, setShowDTData] = useState(null);
  const [openPushOpenLending, setOpenPushOpenLending] = useState(false);
  const [creditReportAlert, setCreditReportAlert] = useState(false);
  const [allowOverride, setAllowOverride] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string[]>([]);
  const [autoLoans, setAutoLoans] = useState<IAutoLoans[]>([]);
  const [openAutoLoansModal, setOpenAutoLoansModal] = useState(0);
  const [openFlagshipOverride, setOpenFlagshipOverride] = useState(0);
  const [showDefaultAlert, setShowDefaultAlert] = useState(false);
  const [showCommentModal, setShowCommentModal] = useState(0);
  const [loanErrors, setLoanErrors] = useState<string[]>([]);

  const { data: lenderUtility } = useSWR(data?.lenderResponse?.lenderId && `${SERVICES.LENDER}/lenders/${data?.lenderResponse?.lenderId}`);

  const isPushApp = useMemo(() => lenderUtility?.isDefi, [lenderUtility]);
  const lenderCode = useMemo(() => data?.lenderResponse?.lenderCode?.trim()?.toUpperCase(), [data?.lenderResponse?.lenderCode]);
  const isMeridian = useMemo(() => meridianLenders.includes(lenderCode ?? ''), [lenderCode]);
  const isPushAppVisible = useMemo(() => lenderCode !== LENDER_CODES.QLO, [lenderCode]);
  const pushToLenderLabel = useMemo(() => (lenderCode === 'CUNJ' ? 'Push CUNJ' : 'Push CUDL'), [lenderCode]);
  const canPushCunj = useMemo(() => lenderCode !== 'CUNJ' || userHasRoles([Roles.PUSH_CUNJ]), [lenderCode, userHasRoles]);

  const handlePushClick = (type: 'standard' | 'dealertrack' | 'openLending') => {
    if (data?.isGrossUpIncome) {
      setPendingPushType(type);
      setShowGrossUpWarning(true);
      return;
    }
    switch (type) {
      case 'standard':
        setOpenPushAppModal(true);
        break;
      case 'dealertrack':
        setOpenPushAppDTModal((prev) => prev + 1);
        break;
      case 'openLending':
        setOpenPushOpenLending(true);
        break;
    }
  };

  const proceedWithPush = () => {
    setShowGrossUpWarning(false);

    switch (pendingPushType) {
      case 'standard':
        setOpenPushAppModal(true);
        break;

      case 'dealertrack':
        setOpenPushAppDTModal((prev) => prev + 1);
        break;

      case 'openLending':
        setOpenPushOpenLending(true);
        break;
    }

    setPendingPushType(null);
  };
  const isCuls = useMemo(
    () => lenderCode === 'CULS',
    [lenderCode]
  );
  
  const handlePushApp = useCallback(async (formikData?: { comment: string }) => {
    if (isMeridian && !showCommentModal) {
      setShowCommentModal(prevState => prevState + 1);
      setOpenPushAppModal(false);
      return;
    }

    if (lenderCode) {
      setLoading(true);
      const payload: IPushAppData = {
        dealId: data?.id,
        overrideHughesData: allowOverride,
        overrideData: allowOverride,
        comment: formikData?.comment
      };
      
      const isCreditRequired = lenderCode === LENDER_CODES.FLAG || lenderCode === LENDER_CODES.GLS || lenderCode === LENDER_CODES.QLO || data?.lenderResponse.isQuilo;
      const response = isCreditRequired ? await requestGetCreditReports(params?.id) : await requestPushApp(payload);

      if (isCreditRequired) {
        if (response?.autoLoans?.length) {
          setOpenAutoLoansModal(prevState => prevState + 1);
          const otherOption = {
            lender: 'other',
            originalAmount: 0,
            openDate: '',
            payment: 0,
            remainingTerm: 0,
          };
          setAutoLoans([...(response?.autoLoans as IAutoLoans[]), otherOption] || []);
        } else {
          setOpenFlagshipOverride(prevState => prevState + 1);
        }
        // eslint-disable-next-line no-dupe-else-if
      } else if (isCreditRequired && !response?.autoLoans?.length) {
        setShowDefaultAlert(true);
      }

      if (!isPushApp) {
        if (response?.isSuccess) {
          setAllowOverride(response?.allowOverride);
          setErrorMessage(response?.errors);
          if (response?.creditReportFail) {
            setCreditReportAlert(true);
          }
          else {
            handleClose();
          }
        } else {
          setLoanErrors(response?.errors ?? []);
        }
      }

      if (!isCreditRequired && !response?.isSuccess && response?.response?.status != 500) {
        const error = getError(response);
        setLoanErrors([error]);
        handleClose();
        setOpenPushAppModal(false);
        setShowCommentModal(0);
        setLoading(false);
        return;
      }

      if (response) {
        dispatch(setRefreshWorkflow(refreshWorkflowSection));
        // setting an empty object as workflow step to refresh the deals table
        // Detecting the change in workflow step to refresh the deals table
        dispatch(setWorkflowNextStep({} as INextStep));
      }
      setLoading(false);
    }
    setOpenPushAppModal(false);
    setShowCommentModal(0);
  }, [
    data,
    allowOverride,
    isPushApp,
    refreshWorkflowSection,
    lenderCode,
    isMeridian,
    showCommentModal
  ]);

  const handlePushToOpenLending = useCallback(async () => {
    setLoading(true);
    const payload: IPushAppData = {
      dealId: data?.id,
    };
    await requestPushToOpenLending(payload);
    dispatch(setRefreshWorkflow(refreshWorkflowSection));
    // setting an empty object as workflow step to refresh the deals table
    // Detecting the change in workflow step to refresh the deals table
    dispatch(setWorkflowNextStep({} as INextStep));
    setOpenPushOpenLending(false);
    setLoading(false);
  }, [data, allowOverride, isPushApp, refreshWorkflowSection]);

  const closePushAppModal = useCallback(() => {
    if (!loading) {
      setOpenPushAppModal(false);
    }
  }, [loading]);

  const handleClose = useCallback(() => {
    if (!loading) {
      if (creditReportAlert) {
        setCreditReportAlert(false);
      } 
    }
  }, [creditReportAlert, loading]);

  const handleCloseOpenLending = useCallback(() => {
    if (!loading) {
      setOpenPushOpenLending(false);
    }
  }, [loading]);

  const handlePushAppDT = useCallback(
    async ({ lenderComment }: ILenderComment) => {
      setLoading(true);
      const payload = {
        dealId: data?.id,
        comment: lenderComment,
      };
      const response = await requestPushAppToDT(payload);

      if (response?.isSuccess) {
        dispatch(setRefreshWorkflow(refreshWorkflowSection));
        // setting an empty object as workflow step to refresh the deals table
        // Detecting the change in workflow step to refresh the deals table
        dispatch(setWorkflowNextStep({} as INextStep));
        setOpenPushAppDTModal(0);
        setShowDTData(response?.applicationNumber);
      }
      setLoading(false);

      if (!response?.isSuccess && response?.response?.status != 500) {
        const error = getError(response);
        setLoanErrors([error]);
        setOpenPushAppDTModal(0);
        return;
      }
      setOpenPushAppDTModal(0);
    },
    [data?.id, refreshWorkflowSection]
  );

  return (
    <>
      {!lenderUtility?.isQuilo && lenderUtility?.isOpenLending && (
        <DealsButton
          onClick={() => {
            data && handlePushClick('openLending');
          }}
        >
          Push to Open Lending
        </DealsButton>
      )}

      {((!lenderUtility?.isQuilo && 
        lenderUtility?.isDealertrack) ? (
          <DealsButton
            disabled={!lenderCode}
            onClick={() => {
              data && handlePushClick('dealertrack');
            }}
          >
            Push App (Dealertrack)
          </DealsButton>
        ) : (
          <DealsButton
            sx={{ display: isPushAppVisible ? 'block' : 'none' }}
            disabled={!lenderCode || !canPushCunj}
            onClick={() => {
              data && canPushCunj && handlePushClick('standard');
            }}
          >
            {isPushApp ? 'Push App' : pushToLenderLabel}
          </DealsButton>
        )
      )}

      {openPushOpenLending && (
        <AlertModal
          title="Push to Open Lending"
          subtitle="Are you sure you want to push this app to Open Lending?"
          maxWidth="350px"
          open={openPushOpenLending}
          isLoading={loading}
          buttonText="Yes"
          cancelButtonText="No"
          onConfirm={handlePushToOpenLending}
          onClose={handleCloseOpenLending}
        />
      )}

  
      {openPushAppModal &&
        (isCuls ? (
          <CulsCudlConfirmationModal
            open={openPushAppModal}
            loading={loading}
            resetPosition={1}
            onConfirm={() => handlePushApp()}
            onClose={closePushAppModal}
          />
        ) : (
          <AlertModal
        title={isPushApp ? 'Push App' : pushToLenderLabel}
          subtitle="Are you sure you want to push this app?"
          maxWidth="350px"
          open={openPushAppModal}
          isLoading={loading}
          buttonText="Yes"
          cancelButtonText="No"
          onConfirm={handlePushApp}
          onClose={closePushAppModal}
          />
        ))}

      {!!showDTData && (
        <AlertModal
          title="Push App (Dealertrack)"
          subtitle={`Application Number: ${showDTData}`}
          open={!!showDTData}
          cancelButtonText="Okay"
          hideSave={true}
          onClose={() => setShowDTData(null)}
        />
      )}

      {!!openPushAppDTModal && (
        <TableModal
          hideBackdrop
          title="Do you want to push this app to Dealertrack?"
          dialogWidth="30vw"
          saveButtonText="submit"
          isOpen={!!openPushAppDTModal}
          resetPosition={openPushAppDTModal}
          editingData={{ lenderComment: '' }}
          isSubmitting={loading}
          formConfig={dealertrackFormConfig}
          onClose={() => setOpenPushAppDTModal(0)}
          onSubmit={handlePushAppDT}
        />
      )}

      {creditReportAlert && (
        <AlertModal
          open={creditReportAlert}
          title={`${creditReportAlert ? 'Credit Report Fail' : 'EquifaxFail'}`}
          hideSave={!allowOverride}
          isLoading={loading}
          buttonText="Override"
          cancelButtonText="Okay"
          subtitle={errorMessage.length ? errorMessage.reduce((acc: string, item: string) => (acc += item + '\n')) : ''}
          onConfirm={handlePushApp}
          onClose={handleClose}
        />
      )}

      {showDefaultAlert && (
        <AlertModal
          hideCancel
          open={showDefaultAlert}
          buttonText="Okay"
          title="Alert"
          subtitle="Error pushing to Defi: Original Loan Amount is Null. LC Lead or Bank Team needed for Override."
          onConfirm={() => setShowDefaultAlert(false)}
          onClose={() => setShowDefaultAlert(false)}
        />
      )}

      {!!openAutoLoansModal && (
        <AutoLoansModal
          autoLoans={autoLoans}
          autoLoansPosition={openAutoLoansModal}
          setOpenAutoLoansModal={setOpenAutoLoansModal}
          data={data}
          refreshDealsTable={refreshDealsTable}
          setLoanErrors={setLoanErrors}
        />
      )}

      {!!openFlagshipOverride && (
        <FlagshipOverrideModal
          flagshipOverrideModalPosition={openFlagshipOverride}
          setOpenFlagshipOverride={setOpenFlagshipOverride}
          data={data}
          refreshDealsTable={refreshDealsTable}
          setLoanErrors={setLoanErrors}
        />
      )}

      {!!showCommentModal && (
        <MeridianCommentModal
          loading={loading}
          meridanModalPosition={showCommentModal}
          handleSubmit={handlePushApp}
          onClose={() => setShowCommentModal(0)}
        />
      )}

      {!!loanErrors.length && (
        <AlertModal
          open
          hideCancel
          buttonText="Okay"
          title="Alert"
          subtitle={loanErrors.reduce((acc: string, item: string) => (acc += item + '\n'))}
          onConfirm={() => setLoanErrors([])}
          onClose={() => setLoanErrors([])}
        />
      )}

      {showGrossUpWarning && (
        <AlertModal
          open={showGrossUpWarning}
          title="Verify Gross Up Income"
          subtitle="Gross Up Income selected. Please verify income is correct before pushing application."
          buttonText="Push App"
          cancelButtonText="Okay"
          onConfirm={proceedWithPush}
          onClose={() => {
            setShowGrossUpWarning(false);
            setPendingPushType(null);
          }}
        />
      )}
    </>
  );
};
